import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthorizerContext';
import {
  MenuDropdownAuthItems,
  MenuDropdownModals,
  useMenuDropdown,
} from './MenuDropdown';
import { MenuInteractor } from './MenuInteractor';
import { RenameTreeModal } from '../tree-management/RenameTreeModal';

interface CompactEditMenuProps {
  isEditMode: boolean;
  isDraft: boolean;
  isLoading: boolean;
  isSaving: boolean;
  showSaveButton: boolean;
  canEdit: boolean;
  treeId?: string;
  treeName: string;
  showRenameTreeInMenu: boolean;
  onRenameTree?: (name: string) => void;
  onSave?: () => void;
  onDeploy?: () => void;
  isDeploying?: boolean;
}

export const CompactEditMenu: React.FC<CompactEditMenuProps> = ({
  isEditMode,
  isDraft,
  isLoading,
  isSaving,
  showSaveButton,
  canEdit,
  treeId,
  treeName,
  showRenameTreeInMenu,
  onRenameTree,
  onSave,
  onDeploy,
  isDeploying = false,
}) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showRenameModal, setShowRenameModal] = useState(false);
  const menuInteractor = useMemo(() => new MenuInteractor(), []);
  const compactMenuState = menuInteractor.getCompactMenuState({
    canEdit,
    hasSaveAction: onSave != null,
    isDraft,
    isEditMode,
    isLoading,
    isSaving,
    showRenameTreeInMenu,
    showSaveButton,
    treeId,
    user,
    hasDeployAction: onDeploy != null,
    isDeploying,
  });
  const {
    closeCreateTreeModal,
    closeLoginModal,
    closeMenu,
    goToMyTrees,
    handleSignOut,
    isOpen,
    menuPanelProps,
    menuRef,
    menuTriggerProps,
    openCreateTreeModal,
    openLoginModal,
    showCreateTreeModal,
    showLoginModal,
    toggleMenu,
    triggerRef,
  } = useMenuDropdown({
    navigate,
    pathname: location.pathname,
    signOut,
  });

  const handleModeToggle = () => {
    closeMenu();
    if (!compactMenuState.modeToggleTarget) return;
    navigate(compactMenuState.modeToggleTarget);
  };

  const auth = compactMenuState.auth;
  const emailHeader =
    auth.showHeader ? (
      <div className="compact-edit-menu-header" role="none" title={auth.email ?? ''}>
        {auth.email}
      </div>
    ) : null;

  const showDraft = compactMenuState.showDraftStatus;
  const hasSaveRow = compactMenuState.showSaveButton && onSave != null;
  const hasModeToggle = compactMenuState.showModeToggle;
  const hasRename = compactMenuState.showRenameTree && onRenameTree != null;
  const hasDeploy = compactMenuState.showDeploy && onDeploy != null;
  const hasCreateTree = auth.showCreateTree;
  const hasMyTrees = auth.showMyTrees;
  const hasPrimaryActions =
    hasSaveRow || hasModeToggle || hasRename || hasDeploy || hasCreateTree || hasMyTrees;
  const hasIdentityHeaderOrDraft = auth.showHeader || showDraft;
  const isGuestMenu = auth.showSignIn;
  const showDividerBeforeTreeActions = isGuestMenu
    ? showDraft && hasPrimaryActions
    : hasIdentityHeaderOrDraft && (hasPrimaryActions || auth.showSignOut);
  const showDividerBeforeAuthTail =
    isGuestMenu && (showDraft || hasSaveRow || hasModeToggle || hasRename || hasDeploy);

  return (
    <div className="compact-edit-menu" ref={menuRef}>
      <button
        type="button"
        ref={triggerRef}
        className="compact-edit-menu-trigger"
        onClick={toggleMenu}
        aria-label="Open edit actions menu"
        {...menuTriggerProps}
      >
        {'\u2630'}
      </button>

      {isOpen && (
        <div className="compact-edit-menu-dropdown" {...menuPanelProps}>
          {emailHeader}
          {showDraft && (
            <div className="compact-edit-menu-status" role="none">
              {compactMenuState.statusLabel}
            </div>
          )}
          {showDividerBeforeTreeActions && (
            <div
              className="compact-edit-menu-divider"
              role="separator"
              aria-orientation="horizontal"
            />
          )}

          {hasSaveRow && (
            <button
              type="button"
              className="compact-edit-menu-item"
              role="menuitem"
              onClick={() => {
                onSave?.();
                closeMenu();
              }}
              disabled={isSaving}
            >
              {compactMenuState.saveButtonLabel}
            </button>
          )}

          {hasModeToggle && (
            <button
              type="button"
              className="compact-edit-menu-item"
              role="menuitem"
              onClick={handleModeToggle}
            >
              {compactMenuState.modeToggleLabel}
            </button>
          )}

          {hasRename && (
            <button
              type="button"
              className="compact-edit-menu-item"
              role="menuitem"
              onClick={() => {
                closeMenu();
                setShowRenameModal(true);
              }}
            >
              Rename tree
            </button>
          )}

          {hasDeploy && (
            <button
              type="button"
              className="compact-edit-menu-item"
              role="menuitem"
              disabled={isDeploying}
              onClick={() => {
                closeMenu();
                onDeploy?.();
              }}
            >
              {compactMenuState.deployLabel}
            </button>
          )}

          {isGuestMenu ? (
            <>
              {showDividerBeforeAuthTail && (
                <div
                  className="compact-edit-menu-divider"
                  role="separator"
                  aria-orientation="horizontal"
                />
              )}
              <MenuDropdownAuthItems
                authState={auth}
                header={undefined}
                dividerClassName="compact-edit-menu-divider"
                itemClassName="compact-edit-menu-item"
                signOutClassName="compact-edit-menu-signout"
                onCreateTree={openCreateTreeModal}
                onMyTrees={goToMyTrees}
                onSignIn={openLoginModal}
                onSignOut={handleSignOut}
              />
            </>
          ) : (
            <>
              {hasCreateTree && (
                <button
                  type="button"
                  className="compact-edit-menu-item"
                  role="menuitem"
                  onClick={openCreateTreeModal}
                >
                  Create Tree
                </button>
              )}
              {hasMyTrees && (
                <button
                  type="button"
                  className="compact-edit-menu-item"
                  role="menuitem"
                  onClick={goToMyTrees}
                >
                  My Trees
                </button>
              )}

              {hasPrimaryActions && auth.showSignOut && (
                <div
                  className="compact-edit-menu-divider"
                  role="separator"
                  aria-orientation="horizontal"
                />
              )}
              {auth.showSignOut && (
                <button
                  type="button"
                  className="compact-edit-menu-item compact-edit-menu-signout"
                  role="menuitem"
                  onClick={handleSignOut}
                >
                  Sign Out
                </button>
              )}
            </>
          )}
        </div>
      )}

      <MenuDropdownModals
        showCreateTreeModal={showCreateTreeModal}
        showLoginModal={showLoginModal}
        onCloseCreateTreeModal={closeCreateTreeModal}
        onCloseLoginModal={closeLoginModal}
      />

      <RenameTreeModal
        isOpen={showRenameModal}
        initialName={treeName}
        onClose={() => setShowRenameModal(false)}
        onConfirm={(name) => {
          onRenameTree?.(name);
        }}
      />
    </div>
  );
};
